/**
 * Bingo Celebration Component
 *
 * Celebration animations for pattern completions.
 */

import { useEffect, useState, useRef } from 'react';
import { CheckCircle2, Sparkles } from 'lucide-react';
import confetti from 'canvas-confetti';

interface BingoCelebrationProps {
  patternType: 'row' | 'column' | 'diagonal_main' | 'diagonal_anti' | 'blackout';
  patternIndex?: number;
  onComplete?: () => void;
}

const patternMessages = {
  row: (index: number) => `Row ${index + 1} Complete! 🎉`,
  column: (index: number) => `Column ${index + 1} Complete! 🎉`,
  diagonal_main: () => 'Main Diagonal Complete! 🎉',
  diagonal_anti: () => 'Anti-Diagonal Complete! 🎉',
  blackout: () => 'BLACKOUT! All Goals Complete! 🎉🎉🎉',
};

export function BingoCelebration({
  patternType,
  patternIndex,
  onComplete,
}: BingoCelebrationProps) {
  const [showMessage, setShowMessage] = useState(true);
  const confettiFired = useRef(false);

  useEffect(() => {
    if (!confettiFired.current) {
      confettiFired.current = true;
      
      // Fire confetti
      const particleCount = patternType === 'blackout' ? 200 : 100;
      
      confetti({
        particleCount,
        spread: 70,
        origin: { y: 0.6 },
      });

      // Fire additional bursts for blackout
      if (patternType === 'blackout') {
        setTimeout(() => {
          confetti({
            particleCount: 100,
            angle: 60,
            spread: 55,
            origin: { x: 0 },
          });
          confetti({
            particleCount: 100,
            angle: 120,
            spread: 55,
            origin: { x: 1 },
          });
        }, 250);
      }
    }

    const timer = setTimeout(() => {
      setShowMessage(false);
      onComplete?.();
    }, patternType === 'blackout' ? 5000 : 3000);

    return () => clearTimeout(timer);
  }, [patternType, onComplete]);

  if (!showMessage) return null;

  const message =
    patternType === 'blackout'
      ? patternMessages.blackout()
      : patternType === 'row' || patternType === 'column'
      ? patternMessages[patternType](patternIndex ?? 0)
      : patternMessages[patternType]();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md mx-4 pointer-events-auto animate-bounce">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            {patternType === 'blackout' ? (
              <Sparkles className="w-16 h-16 text-yellow-500 animate-pulse" />
            ) : (
              <CheckCircle2 className="w-16 h-16 text-green-500" />
            )}
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{message}</h2>
          <p className="text-gray-600">Great work! Keep it up!</p>
        </div>
      </div>
    </div>
  );
}

